/* eslint-disable  @typescript-eslint/no-explicit-any */ // AJB: remove
import PillTextField from "@/app/shared/PillTextField";
import { Box, Divider, InputAdornment, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AspectMultiMenu, { LANDSCAPE_MAX_WIDTH } from "./AspectMultiMenu";
import {
  FlatAspect,
  HomePageSearchResponse,
} from "@/models/HomePageSearchResponse";
import HomePageSearchRequest from "@/models/HomePageSearchRequest";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import AspectSelect from "./AspectSelect";
import DISTANCE_OPTIONS from "@/constants/DistanceOptions";
import AspectClearableLabel from "./AspectClearableLabel";
import { useBodyBackdrop } from "./useBodyBackdrop";
import { useGooglePlaces } from "@/app/layout/useGooglePlaces";

function PlacePrediction({
  onClick,
  children,
}: {
  onClick: () => void;
  children?: React.ReactNode;
}) {
  return (
    <Typography
      sx={(theme) => ({
        cursor: "pointer",
        "@media (hover: hover)": {
          "&:hover": {
            backgroundColor: "grey.100",
          },
          padding: theme.spacing(1, 2),
        },
      })}
      onClick={onClick}
    >
      {children}
    </Typography>
  );
}

export default function AspectLocationMenu({
  searchResponse,
  searchRequest,
  handleOnSearchRequestChange,
}: {
  searchResponse: HomePageSearchResponse;
  searchRequest: HomePageSearchRequest;
  handleOnSearchRequestChange: (request: any, skipRefresh: boolean) => void;
}) {
  const { setBackdropOpen } = useBodyBackdrop();
  const { setInput, options, getPlaceDetails } = useGooglePlaces({
    countryCode: "AU",
  });

  const handleSelectLocation = (
    locations: FlatAspect[] = [],
    label: string = "",
    lat: number = 0,
    lon: number = 0,
  ) => {
    const isLocationSelected = locations.length > 0;
    const isAreaSelected = !!label;
    const skipRefresh = isLocationSelected && !isAreaSelected;
    handleOnSearchRequestChange(
      {
        locations,
        area: {
          ...searchRequest.area,
          label,
          lat,
          lon,
        },
      },
      skipRefresh,
    );
    if (!skipRefresh) {
      setBackdropOpen(false);
    }
  };

  const isAreaSearchSelected = !!searchRequest.area.label;

  return (
    <AspectMultiMenu
      title=""
      label="Location"
      options={searchResponse.locations}
      selectedValues={
        isAreaSearchSelected
          ? [
              {
                id: searchRequest.area.label,
                label: `< ${searchRequest.area.distance} km from ${searchRequest.area.label}`,
              },
            ]
          : searchRequest.locations
      }
      onChange={(locations) => handleSelectLocation(locations)}
      icon={<PlaceOutlinedIcon />}
      hideOptions={isAreaSearchSelected}
      topSlot={
        <Box
          sx={(theme) => ({
            paddingBottom: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            [theme.breakpoints.down(LANDSCAPE_MAX_WIDTH)]: {
              flexDirection: "column",
              padding: 2,
              paddingBottom: 0,
              gap: 1,
            },
          })}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              padding: 2,
              paddingBottom: 0,
            }}
          >
            <Typography variant="h6">Within</Typography>
            <AspectSelect
              aspectKey="distance"
              aspectLabel=""
              value={searchRequest.area.distance}
              options={DISTANCE_OPTIONS}
              onValueChange={(distance) => {
                handleOnSearchRequestChange(
                  {
                    area: {
                      ...searchRequest.area,
                      distance,
                    },
                  },
                  false,
                );
                if (isAreaSearchSelected) {
                  setBackdropOpen(false);
                }
              }}
              fullWidth={false}
              size="small"
              minWidth={100}
            />
            <Typography variant="h6">of:</Typography>
          </Box>
          <Box
            sx={{
              paddingLeft: 2,
              paddingRight: 2,
              paddingBottom: 2,
            }}
          >
            {isAreaSearchSelected ? (
              <AspectClearableLabel
                icon={<PlaceOutlinedIcon />}
                label={""}
                selectedValues={[searchRequest.area.label]}
                onClear={() => handleSelectLocation()}
              />
            ) : (
              <>
                <PillTextField
                  placeholder="Suburb, city or region"
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 100,
                  }}
                  onChange={(evt) => {
                    setInput(evt.target.value);
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          sx={{
                            paddingLeft: 2,
                          }}
                        >
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <Box>
                  {options.map((prediction) => (
                    <PlacePrediction
                      key={prediction.place_id}
                      onClick={async () => {
                        const placeDetails = await getPlaceDetails(prediction);
                        handleSelectLocation(
                          [],
                          placeDetails.description,
                          placeDetails.lat ?? 0,
                          placeDetails.lon ?? 0,
                        );
                      }}
                    >
                      {prediction.description}
                    </PlacePrediction>
                  ))}
                </Box>
              </>
            )}
          </Box>
          {!isAreaSearchSelected && (
            <Divider>
              <Typography>Or select states</Typography>
            </Divider>
          )}
        </Box>
      }
    />
  );
}
